import { ApprovalStatus, Preset } from '../../main/components/Preset/models';
import { Market } from '../../main/components/MarketEditor/interfaces';
import { formatNumberWithCommas, formatCreateDate } from '../../../utils';
import { Role } from '../../../features/authentication/types/auth.types';
import { useAuthStore } from '../../../features/authentication/stores/authStore';

const PresetInfo = ({ preset, markets }: { preset: Preset; markets: Market[] }) => {
  const { hasRole } = useAuthStore();
  // Determine styles and indicators based on approvalStatus
  let indicator = 'Waiting Export Approval'; // Default to pending
  let indicatorStyle = styles.pendingIndicator;

  if (preset.approvalStatus === ApprovalStatus.APPROVED_APPROVER) {
    indicator = 'Approved Discount';
    indicatorStyle = styles.approvedIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.REJECTED_APPROVER) {
    indicator = 'Rejected Discount';
    indicatorStyle = styles.rejectedIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.PRIVATE) {
    indicator = 'Draft';
    indicatorStyle = styles.privateIndicator;
  } else if (preset.approvalStatus === ApprovalStatus.APPROVED_CRO) {
    if (preset.overallDiscount >= 0) {
      indicator = 'Approved Export';
      indicatorStyle = styles.approvedIndicator;
    } else {
      indicator = 'Waiting Discount Approval';
      indicatorStyle = styles.pendingIndicator;
    }
  } else if (preset.approvalStatus === ApprovalStatus.REJECTED_CRO) {
    indicator = 'Rejected Export';
    indicatorStyle = styles.rejectedIndicator;
  }

  const getDiscount = (discount: number) => {
    const formattedDiscount = Math.abs(discount / 2).toFixed(1);
    const sign = discount < 0 ? '-' : discount > 0 ? '+' : '';
    return `${sign}${formattedDiscount}%`;
  };

  const getFee = (fee: number) => {
    return (fee * 0.01).toFixed(2);
  };

  return (
    <div
      key={preset.id}
      style={{
        ...styles.listItem,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <span style={styles.itemName}>{preset.name}</span>
        <div
          style={{
            ...styles.indicator,
            ...indicatorStyle,
          }}
        >
          {indicator}
        </div>
      </div>
      <div style={{ height: '0.4vh' }}></div>
      {hasRole([Role.ADMIN, Role.VIEWER, Role.CRO]) && (
        <div style={styles.itemParam}>
          <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>User</div>
          {preset.user}
        </div>
      )}
      {hasRole([Role.ADMIN, Role.VIEWER, Role.CRO]) && <div style={styles.divider}></div>}
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Company</div>
        {preset.company}
      </div>
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Markets/Units</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.8vh' }}>
          {markets.map((item, index) => (
            <div key={index} style={{ display: 'flex', gap: '0.4vh', width: '8vh' }}>
              <span style={{ fontWeight: '700' }}>{item.market}</span>
              <span>{item.units}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Adjustment Set</div>
        {preset.set}
      </div>
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Paint Provided</div>
        {preset.paintProvided}
      </div>
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Reason for discounts provided</div>
        {preset.reason}
      </div>
      {(preset.approvalStatus === ApprovalStatus.REJECTED_APPROVER ||
        preset.approvalStatus === ApprovalStatus.REJECTED_CRO) && (
        <>
          <div style={styles.divider}></div>
          <div style={styles.itemParam}>
            <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Notes</div>
            {preset.rejectionReason}
          </div>
        </>
      )}
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Created on</div>
        {formatCreateDate(preset.created)}
      </div>
      <div style={styles.divider}></div>
      <div style={styles.itemParam}>
        <div style={{ color: 'rgba(16, 29, 59, 0.6)' }}>Updated on</div>
        {formatCreateDate(preset.lastUpdated)}
      </div>
      <div style={styles.itemBadgeContainer}>
        <div style={{ ...styles.itemBadge }}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Paint
          </div>
          {getDiscount(preset.paintDiscount)}
        </div>
        <div style={{ ...styles.itemBadge }}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Clean
          </div>
          {getDiscount(preset.cleanDiscount)}
        </div>
        <div style={{ ...styles.itemBadge }}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Carpet Clean
          </div>
          {getDiscount(preset.carpetCleanDiscount)}
        </div>
        <div style={{ ...styles.itemBadge }}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Maintenance
          </div>
          {getDiscount(preset.maintenanceDiscount)}
        </div>
        <div style={{ ...styles.itemBadge }}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Margin
          </div>
          {preset.margin}%
        </div>
      </div>
      <div
        style={{
          width: '100%',
          flexDirection: 'row',
          display: 'flex',
          gap: '0.4vh',
          marginTop: '0.4vh',
        }}
      >
        <div style={styles.itemBadge}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Platform Fee
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '0.4vh' }}>
            {preset.saasFeeEnabled === 'Yes' ? (
              <>
                ${getFee(preset.saasUnitFee)}
                <span style={{ fontWeight: '400', fontSize: '1.2vh' }}> (unit/month)</span>
              </>
            ) : (
              'N/A'
            )}
          </div>
        </div>
        {preset.saasFeeEnabled === 'Yes' && (
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Unit Count
            </div>
            {preset.saasContractedUnits.toLocaleString()}
          </div>
        )}
        {preset.saasFeeEnabled === 'Yes' && (
          <div style={styles.itemBadge}>
            <div
              style={{
                fontWeight: '400',
                fontSize: '1.2vh',
              }}
            >
              Total Cost ({preset.saasFeePeriod})
            </div>
            ${formatNumberWithCommas(preset.saasPeriodCost, 2)}
          </div>
        )}
        <div style={styles.itemBadge}>
          <div
            style={{
              fontWeight: '400',
              fontSize: '1.2vh',
            }}
          >
            Overall Discount
          </div>
          {preset.overallDiscount}%
        </div>
      </div>
      <div style={{ minHeight: '0.8vh' }}></div>
    </div>
  );
};

const styles = {
  listItem: {
    padding: '1.6vh',
    borderRadius: '1.6vh',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    boxShadow: '0vh 0.8vh 3.2vh -0.4vh rgba(16, 29, 59, 0.1)',
    fontSize: '1.4vh',
    fontWeight: '400',
    color: 'rgba(16, 29, 59, 1)',
    cursor: 'pointer',
    width: '56.8vh',
  },
  itemName: {
    color: 'rgba(16, 29, 59, 1)',
    fontSize: '1.8vh',
    fontWeight: '700',
  },
  divider: {
    width: '100%',
    height: '0.1vh',
    backgroundColor: 'rgba(16, 29, 59, 0.1)',
    marginTop: '0.8vh',
  },
  itemParam: {
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    paddingTop: '0.8vh',
    gap: '0.2vh',
    textAlign: 'left' as 'left',
  },
  itemBadgeContainer: {
    display: 'flex',
    flexDirection: 'row' as 'row',
    gap: '0.4vh',
    paddingTop: '0.8vh',
    width: '100%',
  },
  itemBadge: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column' as 'column',
    alignItems: 'start',
    padding: '0.8vh',
    borderRadius: '0.8vh',
    color: 'rgba(16, 29, 59, 1)',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
    boxSizing: 'border-box' as 'border-box',
    gap: '0.2vh',
    fontWeight: '700',
    flex: 1,
  },
  percent: {
    fontWeight: '700',
    fontSize: '1.8vh',
  },
  indicator: {
    padding: '0.4vh 0.8vh',
    borderRadius: '0.4vh',
    fontWeight: '700',
    fontSize: '1.4vh',
  },
  approvedIndicator: {
    color: 'rgba(0, 171, 155, 1)',
    backgroundColor: 'rgba(0, 171, 155, 0.05)',
  },
  rejectedIndicator: {
    color: 'rgba(243, 119, 102, 1)',
    backgroundColor: 'rgba(243, 119, 102, 0.05)',
  },
  pendingIndicator: {
    color: 'rgba(4, 129, 200, 1)',
    backgroundColor: 'rgba(4, 129, 200, 0.05)',
  },
  pendingYellowIndicator: {
    color: 'rgba(255, 193, 7, 1)',
    backgroundColor: 'rgba(255, 193, 7, 0.05)',
  },
  privateIndicator: {
    color: 'rgba(16, 29, 59, 1)',
    backgroundColor: 'rgba(16, 29, 59, 0.05)',
  },
};

export default PresetInfo;
